import { customerQuote, loadAddressText, loadAddresCoordinate, showCustomerQuoteWindow, quoteSelectedMaterial } from "./stores.js";
import { formatPhoneNumber, toE164 } from "./phoneUtils.js";
import { fetchQuotes } from "./data.js";
import { encodePolyline } from "./helpers.js";

/**
 * Normalizes an address object or string into a formatted single-line address.
 */
export function normalizeAddressString(addr) {
  if (!addr) return "";
  if (typeof addr === "string") return addr.trim();
  if (addr.place_name) return addr.place_name;
  const parts = [];
  if (addr.street) parts.push(addr.street);
  if (addr.suburb) parts.push(addr.suburb);
  if (addr.state) parts.push(addr.state);
  if (addr.postcode) parts.push(addr.postcode);
  return parts.length > 0 ? parts.join(", ") : (addr.name || "");
}

/**
 * Builds a clean haulagePackage object from a quote material record
 * for use inside the CustomerQuote window.
 */
export function buildHaulagePackageFromMaterial(m, idx = 0, defaultMarginPercent = 15, quote = null) {
  if (!m) return null;

  const loadObj = (m.load && typeof m.load === "object" && !Array.isArray(m.load)) ? m.load : {};
  const fleet = m.fleet || {};
  const sched = m.schedule || {};
  const fin = m.financial_breakdown || {};
  const yard = m.yard || {};
  const opHours = m.operating_hours || {};

  const truckCap = Number(fleet.truck_capacity_tonne || loadObj.truckCapacityTonne || 19);
  const trailerCap = Number(fleet.trailer_capacity_tonne != null ? fleet.trailer_capacity_tonne : (loadObj.trailerCapacityTonne != null ? loadObj.trailerCapacityTonne : 19));
  const totalCapacity = Number(fleet.total_capacity_tonne || (truckCap + trailerCap) || 1);
  const truckType = fleet.truck_type || loadObj.truckType || (truckCap === 12 ? "6 wheeler - 12t" : (truckCap === 15 ? "8 wheeler - 15t" : "10 wheeler - 19t"));
  const trailerType = fleet.trailer_type || loadObj.trailerType || (trailerCap === 0 ? "None" : (trailerCap === 12 ? "4 wheeler - 12t" : (trailerCap === 15 ? "6 wheeler - 15t" : "8 wheeler - 20t")));
  const tonnes = Number(m.amount || m.total_tonnes || m.quantity || loadObj.totalTonnes || 100);
  const runs = Number(sched.runs_per_day ?? loadObj.runs ?? 5);
  const loads = Number(sched.total_loads ?? loadObj.totalLoads ?? (totalCapacity > 0 ? Math.ceil(tonnes / totalCapacity) : 1) ?? 1);
  const allocTrucks = Math.max(1, parseInt(sched.allocated_trucks || (Array.isArray(m.allocated_trucks) ? m.allocated_trucks.length : loadObj.allocatedTrucks) || 1, 10));

  const pkgId = loadObj.id || `pkg_${Date.now()}_${idx}_${Math.floor(Math.random() * 10000)}`;
  const matName = m.material_name || m.name || loadObj.material || `Material ${idx + 1}`;
  const yardId = yard.id || loadObj.yardId || null;
  const yardName = yard.name || loadObj.yardName || m.dispose_address?.name || "Disposal Facility";
  const suburb = yard.suburb || loadObj.suburb || m.dispose_address?.suburb || "";

  let polyline = m.polyline || loadObj.polyline || null;
  if (polyline && typeof polyline === "object") {
    if (Array.isArray(polyline.coordinates)) {
      polyline = encodePolyline(polyline.coordinates);
    } else {
      polyline = null;
    }
  }
  if (!polyline && loadObj.coordinates && Array.isArray(loadObj.coordinates)) {
    polyline = encodePolyline(loadObj.coordinates);
  }

  // Commercial / pricing first-class scalars
  const costPerTonne = m.cost_per_tonne != null ? Number(m.cost_per_tonne) : (fin.base_disposal_rate != null ? Number(fin.base_disposal_rate) : (loadObj.cost_per_tonne || null));
  const unitRate = m.proposed_unit_price != null ? Number(m.proposed_unit_price) : (m.unit_price != null ? Number(m.unit_price) : (fin.effective_rate_per_tonne != null ? Number(fin.effective_rate_per_tonne) : (costPerTonne != null ? costPerTonne : (loadObj.tonneRate || 35))));
  const dayRate = fleet.truck_day_rate != null ? Number(fleet.truck_day_rate) : (loadObj.dayRate || 1400);
  const totalCost = m.total_cost != null ? Number(m.total_cost) : (fin.subtotal_ex_gst != null ? Number(fin.subtotal_ex_gst) : (loadObj.totalCost || (tonnes * unitRate)));
  const proposedTotalPrice = m.proposed_total_price != null ? Number(m.proposed_total_price) : (m.total_price != null ? Number(m.total_price) : totalCost);
  const totalTolls = sched.total_tolls != null ? Number(sched.total_tolls) : (loadObj.totalTolls || 0);
  const marginPercent = m.margin_percent != null ? Number(m.margin_percent) : defaultMarginPercent;

  // Operational first-class scalars (from nested operating_hours and schedule)
  const loadMins = opHours.load_mins != null ? Number(opHours.load_mins) : (loadObj.loadTime != null ? Number(loadObj.loadTime) : 15);
  const tipMins = opHours.tip_mins != null ? Number(opHours.tip_mins) : (loadObj.tipTime != null ? Number(loadObj.tipTime) : 15);
  const estimatedDays = sched.estimated_days != null ? Number(sched.estimated_days) : (loadObj.billedDays || 1);

  // Site / Delivery location loaded strictly to QuoteMaterials.load_address
  const custObj = (quote?.customer && typeof quote.customer === "object") ? quote.customer : null;
  const rawLoad = m.load_address || (custObj?.site_address ? (typeof custObj.site_address === "object" ? custObj.site_address : { street: custObj.site_address }) : (loadObj.load_address || null));
  let matLoadAddress = null;
  if (rawLoad) {
    if (typeof rawLoad === "object") {
      const coords = rawLoad.coordinates ? {
        lat: Number(Array.isArray(rawLoad.coordinates) ? rawLoad.coordinates[1] : (rawLoad.coordinates.lat ?? rawLoad.coordinates.latitude)),
        lng: Number(Array.isArray(rawLoad.coordinates) ? rawLoad.coordinates[0] : (rawLoad.coordinates.lng ?? rawLoad.coordinates.longitude))
      } : null;
      matLoadAddress = {
        ...rawLoad,
        coordinates: (coords && !isNaN(coords.lat) && !isNaN(coords.lng)) ? coords : (rawLoad.coordinates || null),
        place_name: rawLoad.place_name || rawLoad.name || normalizeAddressString(rawLoad)
      };
    } else {
      matLoadAddress = {
        street: String(rawLoad),
        place_name: String(rawLoad),
        suburb: ""
      };
    }
  }
  const matSiteAddress = normalizeAddressString(matLoadAddress) || (typeof m.siteAddress === "string" ? m.siteAddress : "") || (typeof loadObj.siteAddress === "string" ? loadObj.siteAddress : "") || "";

  // Disposal location loaded strictly to QuoteMaterials.disposal_address / dispose_address / yard
  const rawDisposal = m.disposal_address || m.dispose_address || (yard && yard.address ? yard.address : null);
  let matDisposalAddress = null;
  if (rawDisposal) {
    if (typeof rawDisposal === "object") {
      const coords = rawDisposal.coordinates ? {
        lat: Number(Array.isArray(rawDisposal.coordinates) ? rawDisposal.coordinates[1] : (rawDisposal.coordinates.lat ?? rawDisposal.coordinates.latitude)),
        lng: Number(Array.isArray(rawDisposal.coordinates) ? rawDisposal.coordinates[0] : (rawDisposal.coordinates.lng ?? rawDisposal.coordinates.longitude))
      } : null;
      matDisposalAddress = {
        ...rawDisposal,
        coordinates: (coords && !isNaN(coords.lat) && !isNaN(coords.lng)) ? coords : (rawDisposal.coordinates || null),
        place_name: rawDisposal.place_name || rawDisposal.name || normalizeAddressString(rawDisposal)
      };
    } else {
      matDisposalAddress = {
        street: String(rawDisposal),
        place_name: String(rawDisposal),
        suburb: suburb || ""
      };
    }
  }
  const matDisposeAddress = normalizeAddressString(matDisposalAddress) || (typeof m.dispose_address === "string" ? m.dispose_address : normalizeAddressString(m.dispose_address)) || (typeof yard.address === "string" ? yard.address : normalizeAddressString(yard.address)) || "";

  const contactName = m.contact_name ?? m.main_contact?.name ?? yard.contact_name ?? yard.main_contact?.name ?? "";
  const contactPhone = m.contact_phone ?? m.main_contact?.phone ?? yard.phone ?? yard.main_contact?.phone ?? "";
  const contactEmail = m.contact_email ?? m.main_contact?.email ?? yard.email ?? yard.main_contact?.email ?? "";
  const contactPos = m.contact_position ?? m.main_contact?.position ?? yard.position ?? yard.main_contact?.position ?? "";
  const mainContact = m.main_contact || yard.main_contact || (contactName ? { name: contactName, phone: contactPhone, email: contactEmail, position: contactPos } : null);

  return {
    id: pkgId,
    yardId: yardId,
    yardName: yardName,
    yard: (typeof m.yard === "object" && m.yard !== null) ? m.yard : (yard.id || yard.name ? yard : null),
    suburb: suburb,
    image: yard.image || m.image || loadObj.image || null,
    material: matName,
    siteAddress: matSiteAddress,
    load_address: matLoadAddress,
    disposal_address: matDisposalAddress,
    dispose_address: matDisposeAddress,
    contact_name: contactName,
    contact_phone: contactPhone,
    contact_email: contactEmail,
    contact_position: contactPos,
    main_contact: mainContact,
    polyline: polyline,
    coordinates: loadObj.coordinates || null,
    load: {
      ...loadObj,
      polyline: polyline
    },
    tonneRate: costPerTonne != null ? costPerTonne : unitRate,
    totalCapacity: totalCapacity,
    truckCapacityTonne: truckCap,
    trailerCapacityTonne: trailerCap,
    truckType: truckType,
    trailerType: trailerType,
    fleet: {
      ...fleet,
      truck_type: truckType,
      truck_capacity_tonne: truckCap,
      trailer_type: trailerType,
      trailer_capacity_tonne: trailerCap,
      total_capacity_tonne: totalCapacity,
      truck_day_rate: dayRate,
      day_rate: dayRate
    },
    costPerLoad: fin.base_cost_per_load != null ? fin.base_cost_per_load : (loadObj.costPerLoad || (unitRate * totalCapacity)),
    runs: runs,
    tonnesDeliveredInDay: loadObj.tonnesDeliveredInDay || (runs * totalCapacity),
    totalTonnes: tonnes,
    totalLoads: loads,
    totalDays: sched.days_single_truck || loadObj.totalDays || (runs > 0 ? loads / runs : 1),
    billedDays: estimatedDays,
    truckCost: fin.base_cartage_cost != null ? fin.base_cartage_cost : (loadObj.truckCost || 0),
    materialCost: fin.base_material_cost != null ? fin.base_material_cost : (loadObj.materialCost || (tonnes * unitRate)),
    finalCostPerTonne: unitRate,
    dayRate: dayRate,
    dailyTolls: loadObj.dailyTolls || (totalTolls ? Number(totalTolls) : 0),
    tollPerLoad: loadObj.tollPerLoad || 0,
    totalTolls: totalTolls,
    totalCost: proposedTotalPrice,
    cost_per_tonne: costPerTonne,
    proposed_unit_price: unitRate,
    unit_price: unitRate,
    proposed_total_price: proposedTotalPrice,
    total_price: proposedTotalPrice,
    margin_percent: marginPercent,
    isCheapest: !!yard.is_cheapest,
    startTime: opHours.start_time || loadObj.startTime || "07:00",
    finishTime: opHours.finish_time || loadObj.finishTime || "17:00",
    loadTime: loadMins,
    tipTime: tipMins,
    isEstimate: false,
    travelMins: loadObj.travelMins != null ? loadObj.travelMins : null,
    allocatedTrucks: allocTrucks,
    margins: m.margins ? {
      tonneRate: {
        value: Number(m.margins.tonneRate?.value ?? m.margins.tonne_rate?.value ?? 0),
        mode: m.margins.tonneRate?.mode ?? m.margins.tonne_rate?.mode ?? "percent"
      },
      costPerLoad: {
        value: Number(m.margins.costPerLoad?.value ?? m.margins.cost_per_load?.value ?? 0),
        mode: m.margins.costPerLoad?.mode ?? m.margins.cost_per_load?.mode ?? "percent"
      },
      dayRate: {
        value: Number(m.margins.dayRate?.value ?? m.margins.day_rate?.value ?? 0),
        mode: m.margins.dayRate?.mode ?? m.margins.day_rate?.mode ?? "percent"
      },
      cartageCost: {
        value: Number(m.margins.cartageCost?.value ?? m.margins.cartage_cost?.value ?? 0),
        mode: m.margins.cartageCost?.mode ?? m.margins.cartage_cost?.mode ?? "percent"
      },
      totalTolls: {
        value: Number(m.margins.totalTolls?.value ?? m.margins.total_tolls?.value ?? 0),
        mode: m.margins.totalTolls?.mode ?? m.margins.total_tolls?.mode ?? "percent"
      },
      totalCost: {
        value: Number(m.margin_percent ?? m.margins.totalCost?.value ?? m.margins.total_cost?.value ?? defaultMarginPercent),
        mode: m.margins.totalCost?.mode ?? m.margins.total_cost?.mode ?? "percent"
      }
    } : (loadObj.margins || {
      tonneRate: { value: 0, mode: "percent" },
      costPerLoad: { value: 0, mode: "percent" },
      dayRate: { value: 0, mode: "percent" },
      cartageCost: { value: 0, mode: "percent" },
      totalTolls: { value: 0, mode: "percent" },
      totalCost: { value: marginPercent, mode: "percent" }
    }),
    schedule: {
      runs_per_day: runs,
      total_loads: loads,
      allocated_trucks: allocTrucks,
      estimated_days: estimatedDays,
      days_single_truck: sched.days_single_truck || (runs > 0 ? loads / runs : 1),
      full_days: sched.full_days ?? Math.floor(estimatedDays),
      remainder_days: sched.remainder_days ?? 0,
      total_tolls: totalTolls,
      ...sched
    },
    operating_hours: {
      start_time: opHours.start_time || loadObj.startTime || "07:00",
      finish_time: opHours.finish_time || loadObj.finishTime || "17:00",
      load_mins: loadMins,
      tip_mins: tipMins,
      ...opHours
    },
    financial_breakdown: {
      base_disposal_rate: fin.base_disposal_rate ?? (costPerTonne != null ? costPerTonne : 0),
      base_cost_per_load: fin.base_cost_per_load ?? (unitRate * totalCapacity),
      base_day_rate: fin.base_day_rate ?? dayRate,
      base_cartage_cost: fin.base_cartage_cost ?? (loadObj.truckCost || 0),
      base_tolls: fin.base_tolls ?? totalTolls,
      base_material_cost: fin.base_material_cost ?? (loadObj.materialCost || (tonnes * unitRate)),
      base_total_cost: fin.base_total_cost ?? (totalCost || 0),
      quoted_disposal_rate: fin.quoted_disposal_rate ?? unitRate,
      quoted_cost_per_load: fin.quoted_cost_per_load ?? (unitRate * totalCapacity),
      quoted_day_rate: fin.quoted_day_rate ?? dayRate,
      quoted_cartage_cost: fin.quoted_cartage_cost ?? (loadObj.truckCost || 0),
      quoted_tolls: fin.quoted_tolls ?? totalTolls,
      subtotal_ex_gst: fin.subtotal_ex_gst ?? proposedTotalPrice,
      gst: fin.gst ?? (proposedTotalPrice * 0.1),
      total_inc_gst: fin.total_inc_gst ?? (proposedTotalPrice * 1.1),
      effective_rate_per_tonne: fin.effective_rate_per_tonne ?? unitRate,
      margin_added: fin.margin_added ?? 0,
      ...fin
    }
  };
}

/**
 * Transforms an arbitrary Quote object (from API, MongoDB, or UI)
 * into the comprehensive `$customerQuote` store state, and updates
 * relevant stores (customerQuote, loadAddressText, showCustomerQuoteWindow).
 *
 * @param {Object} quote The source quote object
 * @returns {Object} The populated customer quote state
 */
export function loadQuoteIntoCustomerQuoteStore(quote) {
  if (!quote) return null;

  const cust = (quote.customer && typeof quote.customer === "object") ? quote.customer : {};
  const custSum = quote.customer_summary || {};
  const cont = quote.contact || {};
  const addr = custSum.address || cust.address || {};

  const customerName = custSum.name || cust.name || quote.customerName || "";
  const contactPerson = cust.contact_person || custSum.contact_person || cont.name || quote.contactPerson || "";
  const rawPhone = custSum.phone || cust.phone || cont.phone || quote.phone || "";
  const phone = rawPhone ? toE164(rawPhone) : "";
  const email = custSum.email || cust.email || cont.email || quote.email || "";

  // Site address resolution
  let siteAddress = cust.site_address || (typeof custSum.address === "string" ? custSum.address : "") || quote.siteAddress || "";
  if (!siteAddress) {
    siteAddress = normalizeAddressString(addr);
  }
  if (!siteAddress && quote.load_address) {
    siteAddress = normalizeAddressString(quote.load_address);
  }

  const materials = Array.isArray(quote.quote_materials) ? quote.quote_materials : [];
  if (!siteAddress && materials.length > 0 && materials[0].load_address) {
    siteAddress = normalizeAddressString(materials[0].load_address);
  }

  // Quote numbers & dates
  const quoteNumber = quote.quote_number || (quote.id ? `QT-${quote.id}` : (quote.quoteNumber || null));
  const quoteDate = quote.quote_date
    ? quote.quote_date.split("T")[0]
    : (quote.created ? quote.created.split("T")[0] : new Date().toISOString().split("T")[0]);
  const validUntil = quote.valid_until
    ? quote.valid_until.split("T")[0]
    : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
  const notes = quote.notes || "Rates based on standard operating hours (07:00 - 17:00). Tolls and disposal fees included. Subject to clean tipping guidelines.";
  const marginPercent = quote.margin_percent != null ? Number(quote.margin_percent) : 15;

  // Build haulage packages from materials (strictly populating load_address per material)
  const haulagePackages = materials.map((m, idx) => buildHaulagePackageFromMaterial(m, idx, marginPercent, quote)).filter(Boolean);
  const activePkg = haulagePackages[0] || null;
  const primarySiteAddress = activePkg?.siteAddress || haulagePackages[0]?.siteAddress || "";

  const margins = activePkg?.margins || {
    tonneRate: { value: 0, mode: "percent" },
    costPerLoad: { value: 0, mode: "percent" },
    dayRate: { value: 0, mode: "percent" },
    cartageCost: { value: 0, mode: "percent" },
    totalTolls: { value: 0, mode: "percent" },
    totalCost: { value: marginPercent, mode: "percent" }
  };

  const quoteState = {
    id: quote.id || null,
    quoteNumber: quoteNumber,
    quoteDate: quoteDate,
    validUntil: validUntil,
    status: (quote.status || "draft").toLowerCase(),
    customerName: customerName,
    contactPerson: contactPerson,
    prepared_for: contactPerson,
    phone: phone,
    email: email,
    customer: quote.customer || null,
    customer_summary: custSum,
    selectedCompany: (cust && cust.name) ? cust : null,
    companyAddress: addr || null,
    companyPhone: phone || null,
    notes: notes,
    paymentTerms: quote.payment_terms || quote.paymentTerms || "Strictly 14 Days from date of invoice",
    payment_terms: quote.payment_terms || quote.paymentTerms || "Strictly 14 Days from date of invoice",
    marginPercent: marginPercent,
    margins: margins,
    allocatedTrucks: activePkg?.allocatedTrucks || 1,
    haulagePackage: activePkg,
    haulagePackages: haulagePackages,
    activePackageId: activePkg?.id || null,
    activeMaterialTab: activePkg?.material || ""
  };

  // Update stores
  customerQuote.set(quoteState);

  if (primarySiteAddress) {
    loadAddressText.set(primarySiteAddress);
  }

  // If address has coordinates, update map center store
  const coords = addr.coordinates || (typeof quote.load_address === "object" && quote.load_address?.coordinates);
  if (coords && coords.lat && coords.lng) {
    loadAddresCoordinate.set([coords.lng, coords.lat]);
  }

  if (activePkg?.material) {
    quoteSelectedMaterial.set(activePkg.material);
  }

  showCustomerQuoteWindow.set(true);

  return quoteState;
}

/**
 * Fetches an authoritative quote document from the API by ID and loads it into the store.
 * If fetch fails or no ID is provided, gracefully falls back to the provided fallback object.
 *
 * @param {string|number} quoteId ID or quote number
 * @param {Object} [fallbackQuote=null] Fallback quote/job object
 * @returns {Promise<Object>} The loaded quote state
 */
export async function fetchAndLoadQuote(quoteId, fallbackQuote = null) {
  if (quoteId) {
    try {
      const fetched = await fetchQuotes(quoteId);
      const quoteDoc = (fetched && typeof fetched === "object" && !Array.isArray(fetched))
        ? fetched
        : (Array.isArray(fetched) && fetched.length > 0 ? fetched[0] : null);

      if (quoteDoc) {
        return loadQuoteIntoCustomerQuoteStore(quoteDoc);
      }
    } catch (err) {
      console.warn(`[fetchAndLoadQuote] Could not fetch quote #${quoteId} from API:`, err);
    }
  }

  if (fallbackQuote) {
    return loadQuoteIntoCustomerQuoteStore(fallbackQuote);
  }

  return null;
}
